import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, onAuthStateChange, resetPasswordForEmail, updatePassword, resendConfirmationEmail, signInWithOAuth, OAuthProvider } from '../utils/supabase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signInWithProvider: (provider: OAuthProvider) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updateUserPassword: (newPassword: string) => Promise<{ error: any }>;
  resendConfirmation: (email: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Handle auth callback from email confirmation or password reset
    const handleAuthCallback = async () => {
      // Check for hash params (email confirmation, password reset - implicit flow)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const type = hashParams.get('type');
      const refreshToken = hashParams.get('refresh_token');

      // Check for code param (PKCE flow)
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');

      // Determine target URL after auth
      let targetPath = window.location.pathname;
      if (targetPath === '/' || !targetPath) {
        targetPath = '/dashboard';
      }

      try {
        if (code) {
          // PKCE flow - exchange code for session
          console.log('🔐 Processing PKCE auth callback...');
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            console.error('Auth callback error:', error);
          } else {
            console.log('✅ Auth callback successful via PKCE');
            // Redirect to dashboard with email_confirmed flag
            if (data.session) {
              window.location.href = '/dashboard?email_confirmed=true';
              return;
            }
          }
        } else if (accessToken && refreshToken) {
          // Implicit flow - set session
          console.log('🔐 Processing implicit auth callback, type:', type);
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) {
            console.error('Auth callback error:', error);
          } else {
            console.log('✅ Auth callback successful via implicit flow');
            // Redirect to dashboard with email_confirmed flag
            if (data.session) {
              window.location.href = '/dashboard?email_confirmed=true';
              return;
            }
          }
        } else if (type === 'signup' || type === 'recovery' || type === 'email_change') {
          // Sometimes Supabase sends type without tokens in hash
          // Check if we have a valid session already
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            console.log('✅ Session exists after email action:', type);
            window.location.href = '/dashboard?email_confirmed=true';
            return;
          }
        }
      } catch (err) {
        console.error('Auth callback exception:', err);
      }
    };

    // Get initial session
    const getInitialSession = async () => {
      // First, handle any auth callback
      await handleAuthCallback();

      const { data: { session } } = await supabase.auth.getSession();
      
      // Check "Remember me" preference
      // If user didn't choose "remember me" and this is a new browser session (no sessionStorage marker),
      // we should sign them out
      const rememberMe = localStorage.getItem('desta_remember_me');
      const sessionActive = sessionStorage.getItem('desta_session_active');
      
      if (session?.user && !rememberMe && !sessionActive) {
        // User logged in without "remember me" and browser was closed/reopened
        // Sign them out
        await supabase.auth.signOut();
        setUser(null);
        setLoading(false);
        return;
      }
      
      setUser(session?.user ?? null);
      setLoading(false);
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = onAuthStateChange(async (event, session) => {
      console.log('🔄 Auth state changed:', event);
      
      // Handle OAuth callback - process pending "Remember me" preference
      if (event === 'SIGNED_IN' && session?.user) {
        const oauthPending = localStorage.getItem('desta_oauth_pending');
        if (oauthPending !== null) {
          // OAuth login just completed
          if (oauthPending === 'false') {
            // User didn't want to be remembered - set session marker
            sessionStorage.setItem('desta_session_active', 'true');
          }
          // Clean up pending flag
          localStorage.removeItem('desta_oauth_pending');
        }
      }
      
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignUp = async (email: string, password: string, fullName?: string) => {
    setLoading(true);
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    });
    setLoading(false);
    return { error };
  };

  const handleSignIn = async (email: string, password: string) => {
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    setLoading(false);
    return { error };
  };

  const handleSignOut = async () => {
    setLoading(true);
    // Clear "remember me" preferences
    localStorage.removeItem('desta_remember_me');
    sessionStorage.removeItem('desta_session_active');
    const { error } = await supabase.auth.signOut();
    setLoading(false);
    return { error };
  };

  const handleResetPassword = async (email: string) => {
    setLoading(true);
    const { error } = await resetPasswordForEmail(email);
    setLoading(false);
    return { error };
  };

  const handleUpdatePassword = async (newPassword: string) => {
    setLoading(true);
    const { error } = await updatePassword(newPassword);
    setLoading(false);
    return { error };
  };

  const handleResendConfirmation = async (email: string) => {
    setLoading(true);
    const { error } = await resendConfirmationEmail(email);
    setLoading(false);
    return { error };
  };

  const handleSignInWithProvider = async (provider: OAuthProvider) => {
    setLoading(true);
    const { error } = await signInWithOAuth(provider);
    setLoading(false);
    return { error };
  };

  const value: AuthContextType = {
    user,
    loading,
    signUp: handleSignUp,
    signIn: handleSignIn,
    signInWithProvider: handleSignInWithProvider,
    signOut: handleSignOut,
    resetPassword: handleResetPassword,
    updateUserPassword: handleUpdatePassword,
    resendConfirmation: handleResendConfirmation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};